import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { SupportForm } from '@/components/support/SupportDialog';
import { FAQSection, type FaqKey } from '@/components/landing/FAQSection';
import { SUPPORT_EMAIL, SUPPORT_MAILTO, SUPPORT_WHATSAPP_URL } from '@/lib/contact';

// The questions a buyer has before paying (Studio Blueprint 05-F).
const CONTACT_FAQ_KEYS: readonly FaqKey[] = ['approve', 'refund', 'time', 'content', 'commercial'];

// Who runs the site, what it sells and how to reach a person — the page a
// customer (or an ad reviewer) looks for before paying. Copy lives in
// locales/{en,es}.json under contactPage.
export default function Contact() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-6">{t('contactPage.title')}</h1>
            <p className="text-muted-foreground mb-10">{t('contactPage.intro')}</p>

            <div className="grid gap-6 md:grid-cols-2 mb-12">
              <div className="rounded-2xl border border-border p-6">
                <div className="flex items-center gap-2 font-semibold mb-2">
                  <Mail className="h-5 w-5 text-primary" /> {t('contactPage.emailTitle')}
                </div>
                <a href={SUPPORT_MAILTO} className="break-all text-primary underline">{SUPPORT_EMAIL}</a>
                <p className="mt-3 text-sm text-muted-foreground">{t('contactPage.responseTime')}</p>
              </div>
              {SUPPORT_WHATSAPP_URL && (
                <div className="rounded-2xl border border-border p-6" data-testid="contact-whatsapp">
                  <div className="flex items-center gap-2 font-semibold mb-2">
                    <MessageCircle className="h-5 w-5 text-primary" /> {t('contactPage.whatsappTitle')}
                  </div>
                  <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    {SUPPORT_WHATSAPP_URL.replace('https://', '')}
                  </a>
                  <p className="mt-3 text-sm text-muted-foreground">{t('contactPage.whatsappBody')}</p>
                </div>
              )}
              <div className="rounded-2xl border border-border p-6">
                <div className="flex items-center gap-2 font-semibold mb-2">
                  <MessageSquare className="h-5 w-5 text-primary" /> {t('contactPage.aboutTitle')}
                </div>
                <p className="text-sm text-muted-foreground">{t('contactPage.aboutBody')}</p>
              </div>
            </div>

            <h2 className="text-xl font-semibold mb-4">{t('contactPage.formTitle')}</h2>
            <div className="rounded-2xl border border-border p-6 mb-12">
              <SupportForm />
            </div>

            <h2 className="text-xl font-semibold mb-3">{t('contactPage.policiesTitle')}</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><Link to="/pricing" className="underline hover:text-foreground">{t('footer.pricing')}</Link></li>
              <li><Link to="/refunds" className="underline hover:text-foreground">{t('footer.refundPolicy')}</Link></li>
              <li><Link to="/terms" className="underline hover:text-foreground">{t('footer.termsOfService')}</Link></li>
              <li><Link to="/privacy" className="underline hover:text-foreground">{t('footer.privacyPolicy')}</Link></li>
            </ul>
          </div>
        </section>
        <FAQSection keys={CONTACT_FAQ_KEYS} />
      </main>
      <Footer />
    </div>
  );
}
